import React from 'react'

import type {
  ChartSegmentInnerView,
  ChartSegmentLikertDivergingView,
  ChartSegmentMatrixCategoricalView,
  ChartSegmentMatrixRatingView,
} from '@digital-twins/shared-types'

import { chartSegmentHeatTextColor } from './heatmap-color'

import { getChartInsideLabelColor } from '../../foundations/chart-colors'
import { roundHalfAwayFromZero } from '../../foundations/producer-rounding'
import { SlideNotice } from '../../slides/SlideNotice'
import { RATING_BAND_STRONG_FILL } from '../../plots/diverging-likert/bar-plan'
import type { RatingBandTone } from '../../plots/diverging-likert/bar-plan'
import { DivergingLikertPlot } from '../../plots/diverging-likert/DivergingLikertPlot'
import { readStampedDivergingLikert } from '../../plots/diverging-likert/adapters'
import { resolveRatingBar } from '../../plots/diverging-likert/legacy/rating-likert-split'
import { HeatGridPlot } from '../../plots/heat-grid/HeatGridPlot'
import type { HeatGridPlotData } from '../../plots/heat-grid/HeatGridPlot'
import { matrixRatingSlideAdapter, readStampedHeatGrid } from '../../plots/heat-grid/readAdapters'
import { QuadrantPlot } from '../../plots/quadrant/QuadrantPlot'
import { readStampedQuadrant } from '../../plots/quadrant/adapters'
import {
  CategoricalShareLegend,
  GroupedBarsGroups,
  StackedHundredBarsRows,
  readStoredMatrixCategoricalView,
} from '../../plots/categorical-share'
import type {
  CategoricalShareKind,
  CategoricalSharePayload,
  CategoricalSharePlotData,
  StoredMatrixCategoricalView,
} from '../../plots/categorical-share'
import type { MatrixRatingSlideData } from '../../slides/renderer-internal-types'

const MINT = 'var(--slide-diverging-positive)'
const RED = 'var(--slide-diverging-negative)'
/**
 * Percentage of the whole track below which a band is narrower than its own
 * caption. Printing one there smears the number across its neighbours, so the
 * figure moves to the band's tooltip instead of being drawn.
 */
const LIKERT_CAPTION_MIN_TRACK_SHARE = 5

function likertFigure(value: number) {
  return Number.isInteger(value) ? value.toFixed(0) : value.toFixed(1)
}

/**
 * The caption colour for a band, decided from the fill the band is actually drawn
 * with. A soft band is a thin wash of its colour over the slide surface, so what the
 * caption has to contrast with is the surface — the same reasoning, and the same
 * manual mix, that `chartSegmentHeatTextColor` applies to the palest heatmap cells.
 * Reading the token instead returns null on a color-mix and silently yields white.
 *
 * Exported because the slide's own bar row draws the same bands from the same tone
 * table: one rule for which captions are ink and which are paper, not two.
 */
export function ratingSegmentCaptionColor(tone: RatingBandTone): string {
  if (tone.fill >= RATING_BAND_STRONG_FILL) {
    return getChartInsideLabelColor(tone.color)
  }
  return chartSegmentHeatTextColor(tone.fill)
}

// -- legacy: an unstamped likert tab (a response from an analysis service that
// predates the tab plot) keeps today's inner view and its reader, which hold
// the rating-semantics invariants for that shape (unavailable reads N/A, never
// a measured zero). Freed once every analysis service stamps the tab plot.

/**
 * One coloured band of the diverging track, captioned with the share it measured.
 *
 * The band prints its own value and nothing else. It used to print a figure the
 * renderer had recomputed from the measured groups, because the two soft bands were
 * each drawn with half the neutral bucket and had no honest number of their own; the
 * builder now stamps a middle band as one segment, so the drawn width and the printed
 * share are the same quantity again.
 */
function ChartSegmentLikertBand(props: {
  segmentId: string
  band: string
  label: string
  /** Percent of the whole track this band occupies, used to place and fit it. */
  left: number
  width: number
  value: number
  tone: RatingBandTone
}) {
  const rounded = roundHalfAwayFromZero(props.value)
  const fits = rounded > 0 && props.width >= LIKERT_CAPTION_MIN_TRACK_SHARE

  return (
    <div
      data-testid="chart-segment-likert-band"
      data-segment={props.segmentId}
      data-band={props.band}
      title={`${props.label}: ${rounded}%`}
      style={{
        position: 'absolute',
        left: `${props.left}%`,
        top: 0,
        width: `${props.width}%`,
        height: 28,
        minWidth: 0,
        background: props.tone.color,
        color: ratingSegmentCaptionColor(props.tone),
        fontSize: 10,
        fontWeight: 600,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
      }}
    >
      {fits ? `${rounded}%` : ''}
    </div>
  )
}

// legacy reader only: a segment response stored before tab plots (every new
// response draws `ChartSegmentLikertPlot`'s stamped plot, labels Python's).
function ChartSegmentLikertInner(props: {
  view: ChartSegmentLikertDivergingView
  displayLabels?: Record<string, string>
}) {
  const { view } = props
  // One resolver for the slide, its rows and both drilldowns. A response that
  // carries `segments` is drawn exactly as the builder stamped it, with or without
  // the `ratingStatistics` the request's semantics gate may have withheld; a
  // response cached before the field existed falls to the legacy three-band bar.
  //
  // The Favorable / Neutral / Unfavorable line reads `plan.groups`, summed from
  // the segments the track paints, rather than re-splitting the histogram in
  // TypeScript. On a response carrying `segments` but no `ratingStatistics` the
  // old read was a scale-midpoint split done here, which on 1-7 counted a `5` as
  // favorable while the bar beside it drew that same `5` as middle. The legacy
  // split still supplies the bar, and therefore the line, for a response that
  // carries neither field.
  const bar = resolveRatingBar(view, props.displayLabels)
  if (bar.dataStatus === 'unsupported') {
    return props.displayLabels?.unsupportedScale ? <SlideNotice>{props.displayLabels.unsupportedScale}</SlideNotice> : null
  }
  const plan = bar.plan
  if (!plan) {
    return null
  }
  const maxSide = Math.max(plan.leftTotal, plan.rightTotal, 1)
  // Both halves are scaled against the same dominant side, so a band's share of
  // the track is its own value against that side, halved across the midline.
  const leftFill = (plan.leftTotal / maxSide) * 50
  const rightFill = (plan.rightTotal / maxSide) * 50
  let cursor = 50 - leftFill
  const boxes = plan.pieces.map((piece) => {
    const width =
      (plan.leftTotal > 0 ? (piece.leftValue / plan.leftTotal) * leftFill : 0) +
      (plan.rightTotal > 0 ? (piece.rightValue / plan.rightTotal) * rightFill : 0)
    const left = cursor
    cursor += width
    return { piece, left, width }
  })
  // Says what was measured, in the order the track draws it. Every segment names
  // itself now, so there is no half-bucket left to explain away.
  const accessibleLabel = plan.pieces
    .filter((piece) => piece.value > 0)
    .map((piece) => `${piece.label}: ${roundHalfAwayFromZero(piece.value)}%`)
    .join(', ')
  const net = plan.net
  const netRounded = roundHalfAwayFromZero(net)
  const topBox =
    typeof view.topBoxPercent === 'number' && Number.isFinite(view.topBoxPercent)
      ? view.topBoxPercent
      : null
  const mean = typeof view.mean === 'number' && Number.isFinite(view.mean) ? view.mean : null

  return (
    <div
      data-testid="chart-segment-likert-inner"
      style={{ padding: '8px 12px 0', boxSizing: 'border-box' }}
    >
      <div
        data-testid="chart-segment-likert-metrics"
        style={{ display: 'flex', justifyContent: 'center', gap: 20, flexWrap: 'wrap', color: 'var(--slide-text-secondary)', fontSize: 12 }}
      >
        {plan.groups.map((group) => (
          <span key={group.band}>
            {/* legacy reader only: a response stored before tab plots. */}
            {group.label}: <strong style={{ color: 'var(--slide-text-primary)' }}>{bar.dataStatus === 'available' ? `${roundHalfAwayFromZero(group.value)}%` : 'N/A'}</strong>
          </span>
        ))}
      </div>
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 72 }}>
        <div
          data-testid="chart-segment-likert-track"
          role="img"
          // legacy reader only: a response stored before tab plots.
          aria-label={bar.dataStatus === 'available' ? accessibleLabel : bar.dataStatus === 'empty' ? 'No rating answers' : 'Rating statistics unavailable'}
          style={{ width: '100%', maxWidth: 520, height: 28, position: 'relative' }}
        >
          {boxes.map(({ piece, left, width }) => (
            piece.value > 0 && width > 0 ? (
              <ChartSegmentLikertBand
                key={`${piece.index}-${piece.label}`}
                segmentId={String(piece.index)}
                band={piece.band}
                label={piece.label}
                left={left}
                width={width}
                value={piece.value}
                tone={piece.tone}
              />
            ) : null
          ))}
          <div style={{ position: 'absolute', left: '50%', top: -6, bottom: -6, width: 1, background: 'var(--slide-text-primary)' }} />
        </div>
      </div>
      <div
        data-testid="chart-segment-likert-summary"
        style={{ display: 'flex', justifyContent: 'center', gap: 16, flexWrap: 'wrap', marginBottom: 6, color: 'var(--slide-text-secondary)', fontSize: 11 }}
      >
        <span>
          {/* legacy reader only: a response stored before tab plots. */}
          {props.displayLabels?.net || 'Net'}{' '}
          <strong data-testid="chart-segment-likert-net" style={{ color: net < 0 ? RED : MINT }}>
            {bar.dataStatus !== 'available' ? 'N/A' : netRounded > 0 ? `+${netRounded}` : String(netRounded)}
          </strong>
        </span>
        {bar.dataStatus === 'available' && topBox !== null ? (
          <span data-testid="chart-segment-likert-topbox">
            Top box{' '}
            {/*
              A percentage, so it prints whole and half away from zero like every other
              figure on this slide. `likertFigure` still serves the mean below,
              which is a point on the rating scale and not a share: rounding 3.7
              to 4 would lose the only thing a mean says.
            */}
            <strong style={{ color: 'var(--slide-text-primary)' }}>{roundHalfAwayFromZero(topBox)}%</strong>
          </span>
        ) : null}
        {bar.dataStatus === 'available' && mean !== null ? (
          <span data-testid="chart-segment-likert-mean">
            Mean{' '}
            <strong style={{ color: 'var(--slide-text-primary)' }}>{likertFigure(mean)}</strong>
          </span>
        ) : null}
      </div>
      <div
        data-testid="chart-segment-likert-legend"
        style={{ display: 'flex', justifyContent: 'center', gap: 14, flexWrap: 'wrap', color: 'var(--slide-text-secondary)', fontSize: 11 }}
      >
        {plan.legend.map((entry) => (
          <span key={entry.band} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
            <span
              data-testid="chart-segment-likert-legend-swatch"
              data-segment={entry.band}
              aria-hidden="true"
              style={{
                width: 8,
                height: 8,
                borderRadius: 2,
                background: entry.color,
              }}
            />
            {entry.label}
          </span>
        ))}
      </div>
    </div>
  )
}

/**
 * A tab's likert through the plot library: the tab's stamped plot (figures
 * Python's), else the legacy inner view above.
 */
function ChartSegmentLikertPlot(props: {
  view: ChartSegmentLikertDivergingView
  displayLabels?: Record<string, string>
  plot?: unknown
}) {
  const { view, displayLabels, plot } = props

  const data = readStampedDivergingLikert(plot)

  if (!data) {
    return <ChartSegmentLikertInner view={view} displayLabels={displayLabels} />
  }
  return (
    <div data-testid="chart-segment-likert-inner" style={{ padding: '8px 12px 0', boxSizing: 'border-box' }}>
      <DivergingLikertPlot data={data} testIdPrefix="chart-segment-likert" showScaleEnds />
    </div>
  )
}

/**
 * A tab's rating grid through the plot library: the tab's stamped heat-grid,
 * else its view through the matrix-rating read adapter.
 */
function ChartSegmentMatrixRatingPlot(props: { view: ChartSegmentMatrixRatingView; plot?: unknown }) {
  const { view, plot } = props

  const grid =
    readStampedHeatGrid(plot) ??
    matrixRatingSlideAdapter.read({
      type: 'matrix-rating',
      measures: view.measures.map((measure) => ({
        id: measure.id,
        label: measure.label,
        metricId: measure.id,
        unit: measure.unit || '',
        polarity: measure.polarity,
        min: measure.min,
        max: measure.max,
      })),
      rows: view.rows.map((row) => ({
        id: row.id,
        label: row.label,
        average: typeof row.average === 'number' ? row.average : null,
        cells: row.cells.map((cell) => ({
          measureId: cell.measureId,
          value: cell.value,
          normalizedValue: cell.normalizedValue,
        })),
      })),
      scale: view.scale as MatrixRatingSlideData['scale'],
    } as Parameters<typeof matrixRatingSlideAdapter.read>[0])

  return (
    <div data-testid="chart-segment-matrix-rating-inner" style={{ maxWidth: '100%', padding: 8, boxSizing: 'border-box' }}>
      <HeatGridPlot
        data={grid.data as HeatGridPlotData}
        props={grid.props}
        testIdPrefix="chart-segment-matrix-rating"
      />
    </div>
  )
}

/**
 * A tab's categorical exhibit through the plot library (plots plan L): the
 * tab's stamped `plot` when present, else its view through the read adapter.
 * Shares are drawn as computed, never re-normalised on the row sum.
 */
function ChartSegmentCategoricalSharePlot(props: { view: ChartSegmentMatrixCategoricalView; plot?: unknown }) {
  const { view, plot } = props

  const stamped =
    typeof plot === 'object' && plot !== null && ['stacked-100-bars', 'grouped-bars'].includes(String((plot as { kind?: unknown }).kind))
      ? (plot as CategoricalSharePayload)
      : null
  const payload = stamped ?? readStoredMatrixCategoricalView(view as StoredMatrixCategoricalView)
  const kind = payload.kind as CategoricalShareKind
  const data = payload.data as CategoricalSharePlotData

  return (
    <div
      data-testid="chart-segment-matrix-categorical-inner"
      style={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column', gap: 12, boxSizing: 'border-box', overflowY: 'auto', scrollbarWidth: 'thin' }}
    >
      {kind === 'grouped-bars' ? (
        <GroupedBarsGroups data={data} size="slide" />
      ) : (
        <StackedHundredBarsRows data={data} size="slide" testIdPrefix="chart-segment-category" />
      )}
      <CategoricalShareLegend data={data} kind={kind} size="slide" testIdPrefix="chart-segment-category" />
    </div>
  )
}

/**
 * One segment's positioning map: the quadrant plot Python stamped on the tab
 * (the producer the slide, the summary card and quick study draw). The frame
 * is fixed by the whole sample, so the same asset in different places across
 * tabs is a real difference in the segments, and assets that score alike in a
 * cut share a spot, so their names stack instead of overprinting. Segment
 * views are requested live and Python stamps every tab, so a tab without its
 * plot draws nothing.
 */
function ChartSegmentQuadrantPlot(props: { plot?: unknown }) {
  const stamped = readStampedQuadrant(props.plot)

  if (!stamped) {
    return null
  }
  return (
    <div data-testid="chart-segment-quadrant" style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <QuadrantPlot data={stamped.data} props={stamped.props} testId="chart-segment-quadrant-plot" stackCoincident />
    </div>
  )
}

export function ChartSegmentInnerViewRenderer(props: {
  view: ChartSegmentInnerView
  displayLabels?: Record<string, string>
  /** The tab's stamped plot (plots plan D1), which every kind draws. */
  plot?: unknown
}) {
  const { view, displayLabels, plot } = props
  if (view.kind === 'likert-diverging') {
    return <ChartSegmentLikertPlot view={view} displayLabels={displayLabels} plot={plot} />
  }
  if (view.kind === 'matrix-rating') {
    return <ChartSegmentMatrixRatingPlot view={view} plot={plot} />
  }
  if (view.kind === 'matrix-categorical') {
    return <ChartSegmentCategoricalSharePlot view={view} plot={plot} />
  }
  if (view.kind === 'quadrant') {
    return <ChartSegmentQuadrantPlot plot={plot} />
  }
  return null
}
